import React, { useMemo, useState } from 'react';
import { Search, Calendar, Boxes, ChevronDown } from 'lucide-react';
type Status = 'Open' | 'Closing Soon' | 'Closed';
interface Bid {
  id: string;
  title: string;
  category: string;
  description: string;
  published: string;
  closingDate: string;
  status: Status;
  lots: string[];
}
const BIDS: Bid[] = [{
  id: 'AU-KHE-2026-024',
  title: 'Kitchen and Household Equipment AU Framework Agreement',
  category: 'Goods',
  description: 'Kitchen cabinets, kitchen countertops, beds, chairs, and stools — typically fall under general furniture and fittings categories.',
  published: '20 Jun 2026',
  closingDate: '04 Aug 2026',
  status: 'Open',
  lots: ['Kitchen cabinets', 'Kitchen countertops', 'Beds', 'Chairs', 'Stools']
}, {
  id: 'AU-TEX-2026-023',
  title: 'Framework Agreement for Textiles and Home Textile Products',
  category: 'Goods',
  description: 'Part of the 2026 annual projects of the African Union (AU), covering the supply of textiles and home textile products in support of regional integration, industrialization, and sustainable development across member states.',
  published: '19 Jun 2026',
  closingDate: '02 Aug 2026',
  status: 'Open',
  lots: ['Apparel and general textiles', 'Home textile products (linens, furnishings, and related goods)', 'Medical uniforms', 'Polo shirts', 'Trousers', 'Workwear', 'Sportswear', 'Uniforms']
}, {
  id: 'AU-HN-2026-022',
  title: 'Framework Agreement for Health and Nutrition',
  category: 'Goods',
  description: 'Encompass a broad range of sectors, including food and dairy products, wine, spirits, cosmetics, biscuits, and cereals. The key priorities and needs for these sectors typically align with the AU’s overarching goals of boosting regional integration, food security, industrialization, and sustainable development.',
  published: '18 Jun 2026',
  closingDate: '31 Jul 2026',
  status: 'Open',
  lots: ['Food and dairy products', 'Wine and spirits', 'Cosmetics', 'Biscuits and cereals']
}, {
  id: 'AU-GOODS-2026-021',
  title: 'Electrical and Household Appliance Framework Agreement',
  category: 'Goods',
  description: 'Framework agreement for the supply of electrical and household appliances. Electrical Equipment and Devices: encompassing items like electric timers and appliances with significant power consumption. Kitchen and Cooking Equipment: gas burners and related cooking appliances.',
  published: '16 Jun 2026',
  closingDate: '24 Jul 2026',
  status: 'Open',
  lots: ['Electrical Equipment and Devices: Encompassing items like electric timers and appliances with significant power consumption.', 'Kitchen and Cooking Equipment: Gas burners and related cooking appliances.']
}, {
  id: 'AU-AFRI-2026-007',
  title: 'Framework Agreement for Women Entrepreneurs Support Programme (Lots 1–3)',
  category: 'Services',
  description: 'Multi-year framework to deliver a comprehensive support programme for women entrepreneurs across member states, awarded across three independent lots. Women-led organisations and SMEs are strongly encouraged to apply.',
  published: '12 Apr 2026',
  closingDate: '08 May 2026',
  status: 'Closing Soon',
  lots: ['Lot 1 — Business advisory & mentorship', 'Lot 2 — Access-to-finance facilitation', 'Lot 3 — Market access & trade missions']
}, {
  id: 'AU-AFRI-2026-001',
  title: 'Framework Agreement for Medical Equipment Supply to Health Partnership Programme (Lots 1–4)',
  category: 'Goods',
  description: 'Framework agreement for the supply of medical equipment in support of the Health Systems Strengthening Initiative across 15 member states. Suppliers may bid on one or more lots; all equipment must meet international medical-device safety standards.',
  published: '02 Apr 2026',
  closingDate: '30 May 2026',
  status: 'Open',
  lots: ['Lot 1 — Diagnostic imaging', 'Lot 2 — Laboratory equipment', 'Lot 3 — Surgical instruments', 'Lot 4 — Cold-chain & storage']
}, {
  id: 'AU-INF-2026-014',
  title: 'Construction of Solar Mini-Grids in Rural Communities',
  category: 'Works',
  description: 'Design, supply, and installation of solar mini-grids to provide reliable clean energy to underserved rural communities, including ongoing maintenance and community training components.',
  published: '20 Mar 2026',
  closingDate: '15 May 2026',
  status: 'Open',
  lots: ['Lot 1 — Northern region sites', 'Lot 2 — Eastern region sites']
}, {
  id: 'AU-IT-2026-009',
  title: 'Supply and Installation of IT Equipment for Regional Offices',
  category: 'Goods',
  description: 'Procurement, delivery, and configuration of networking and end-user IT equipment across regional offices, with a three-year support and warranty arrangement.',
  published: '11 Mar 2026',
  closingDate: '02 Apr 2026',
  status: 'Closed',
  lots: ['Lot 1 — Networking hardware', 'Lot 2 — End-user devices']
}, {
  id: 'AU-EDU-2026-005',
  title: 'Procurement of Educational Materials for Vocational Centers',
  category: 'Goods',
  description: 'Supply of curriculum-aligned educational and training materials for vocational centers, supporting youth skills development and employability programmes.',
  published: '05 Mar 2026',
  closingDate: '28 May 2026',
  status: 'Open',
  lots: ['Lot 1 — Print & digital learning materials', 'Lot 2 — Workshop tools & kits']
}, {
  id: 'AU-HLTH-2026-002',
  title: 'Consultancy Services for Health Infrastructure Assessment',
  category: 'Services',
  description: 'Engagement of a qualified consultancy to conduct a continent-wide assessment of health infrastructure capacity and produce an investment-readiness roadmap.',
  published: '28 Feb 2026',
  closingDate: '06 May 2026',
  status: 'Closing Soon',
  lots: ['Lot 1 — Field assessment', 'Lot 2 — Analysis & reporting']
}];
const STATUSES = ['All Status', 'Open', 'Closing Soon', 'Closed'];
const CATEGORIES = ['All Categories', 'Goods', 'Works', 'Services'];
const statusStyles: Record<Status, string> = {
  Open: 'bg-green-100 text-au-green',
  'Closing Soon': 'bg-amber-100 text-amber-700',
  Closed: 'bg-gray-200 text-gray-600'
};
const selectClass = 'w-full rounded border border-gray-300 bg-white px-4 py-2.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-au-green focus:border-transparent';
export function BidsTenders() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('All Status');
  const [category, setCategory] = useState('All Categories');
  const [expanded, setExpanded] = useState<string | null>(null);
  const filtered = useMemo(() => {
    return BIDS.filter(bid => {
      const matchesSearch = search.trim() === '' || bid.title.toLowerCase().includes(search.toLowerCase()) || bid.id.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = status === 'All Status' || bid.status === status;
      const matchesCategory = category === 'All Categories' || bid.category === category;
      return matchesSearch && matchesStatus && matchesCategory;
    });
  }, [search, status, category]);
  return <section id="bids" className="py-16 md:py-20 bg-gray-50" data-id="element-37">
      <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-38">
        {/* Filter bar */}
        <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-6 mb-8" data-id="element-39">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-40">
            <div data-id="element-41">
              <label htmlFor="bid-search" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-42">
                Search
              </label>
              <div className="relative" data-id="element-43">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-44" />
                <input id="bid-search" type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search bids..." className="w-full rounded border border-gray-300 bg-white pl-10 pr-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-au-green focus:border-transparent" data-id="element-45" />
              </div>
            </div>
            <div data-id="element-46">
              <label htmlFor="bid-status" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-47">
                Status
              </label>
              <select id="bid-status" value={status} onChange={e => setStatus(e.target.value)} className={selectClass} data-id="element-48">
                {STATUSES.map(s => <option key={s} data-id="element-49">{s}</option>)}
              </select>
            </div>
            <div data-id="element-50">
              <label htmlFor="bid-category" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-51">
                Category
              </label>
              <select id="bid-category" value={category} onChange={e => setCategory(e.target.value)} className={selectClass} data-id="element-52">
                {CATEGORIES.map(c => <option key={c} data-id="element-53">{c}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Results count */}
        <p className="text-gray-600 mb-6" data-id="element-54">
          Showing{' '}
          <span className="font-bold text-au-navy" data-id="element-55">{filtered.length}</span>{' '}
          {filtered.length === 1 ? 'bid' : 'bids'}
        </p>

        {/* Bid cards */}
        {filtered.length === 0 ? <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-12 text-center text-gray-500" data-id="element-56">
            No bids match your filters. Try adjusting your search or filters.
          </div> : <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-57">
            {filtered.map(bid => {
          const isOpen = expanded === bid.id;
          return <article key={bid.id} className="bg-white rounded-lg border border-gray-100 shadow-sm p-6 flex flex-col" data-id="element-58">
                  <div className="flex items-start justify-between gap-4 mb-3" data-id="element-59">
                    <span className="font-mono text-xs text-gray-500 tracking-wide" data-id="element-60">
                      {bid.id}
                    </span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider whitespace-nowrap ${statusStyles[bid.status]}`} data-id="element-61">
                      {bid.status}
                    </span>
                  </div>

                  <h3 className="text-lg font-bold text-au-navy leading-snug mb-3" data-id="element-62">
                    {bid.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-grow" data-id="element-63">
                    {bid.description}
                  </p>

                  <button type="button" onClick={() => setExpanded(isOpen ? null : bid.id)} aria-expanded={isOpen} className="inline-flex items-center self-start text-au-green font-medium text-sm hover:text-au-navy transition-colors focus:outline-none focus:ring-2 focus:ring-au-green focus:ring-offset-2 rounded-sm" data-id="element-64">
                    <Boxes size={16} className="mr-2" data-id="element-65" />
                    View lot breakdown ({bid.lots.length}{' '}
                    {bid.lots.length === 1 ? 'lot' : 'lots'})
                    <ChevronDown size={16} className={`ml-1 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-66" />
                  </button>

                  {isOpen && <ul className="mt-3 space-y-2 border-l-2 border-green-100 pl-4" data-id="element-67">
                      {bid.lots.map((lot, i) => <li key={i} className="text-sm text-gray-700" data-id="element-68">
                          {lot}
                        </li>)}
                    </ul>}

                  <div className="flex flex-wrap gap-x-6 gap-y-1 mt-4 pt-4 border-t border-gray-100 text-xs text-gray-500" data-id="element-69">
                    <span className="inline-flex items-center" data-id="element-70">
                      <Calendar size={14} className="mr-1.5 text-au-green" data-id="element-71" />
                      Published: {bid.published}
                    </span>
                    <span className="inline-flex items-center" data-id="element-72">
                      <Calendar size={14} className="mr-1.5 text-au-green" data-id="element-73" />
                      Closing: {bid.closingDate}
                    </span>
                  </div>
                </article>;
        })}
          </div>}
      </div>
    </section>;
}